/**
 * NavGroupDropdown — top-nav dropdown grouping cluster of secondary
 * pages (Learn / Watch / Tools, etc.) into a single menu so the inline
 * navbar stays focused on flagship products.
 *
 * Renders a button with chevron that opens a Radix DropdownMenu containing
 * the supplied items. Each item is a small NavLink-like row with optional
 * leading icon + short subtitle.
 *
 * Used by `Navbar.jsx`.
 */
import { NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ITEM_BASE = "flex items-start gap-2 rounded-md px-2.5 py-2 text-sm transition-colors";
const ITEM_INACTIVE = "text-rp-textDim hover:bg-rp-surface2 hover:text-rp-text";
const ITEM_ACTIVE = "bg-rp-surface2 text-rp-text";

const itemClass = ({ isActive }) =>
  `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_INACTIVE}`;

export default function NavGroupDropdown({
  label, items, testId, onItemClick,
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="text-sm text-rp-textDim hover:text-rp-text transition-colors inline-flex items-center gap-1 whitespace-nowrap focus:outline-none"
          data-testid={`nav-group-${testId}`}
        >
          {label}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="bg-rp-surface border-rp-border text-rp-text min-w-[220px] p-1.5"
      >
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={itemClass}
            onClick={onItemClick}
            data-testid={it.testId}
          >
            {it.icon && (
              <span className="grid h-7 w-7 place-items-center rounded-md bg-rp-bg/60 border border-rp-border text-rp-textDim shrink-0">
                {it.icon}
              </span>
            )}
            <span className="min-w-0">
              <span className="block font-medium text-rp-text">{it.label}</span>
              {it.hint && (
                <span className="block text-[11px] text-rp-textFaint mt-0.5">
                  {it.hint}
                </span>
              )}
            </span>
          </NavLink>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
